import { Modal } from '@mantine/core';
import {
  useJournalEntriesQuery,
  useJournalEntriesModal,
  useJournalEntriesModalFilter,
  useJournalEntriesModalWhere,
} from '@vrplatform/embed';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { GeneralLedgerExportButton } from '../../views/general-ledger/GeneralLedgerExportButton';
import { JournalEntriesTable } from './JournalEntriesTable';
import { HiddenFeatureIndicator } from '../../components/HiddenFeatureIndicator';

type FilterProps = ComponentProps<typeof JournalEntriesTable>['filterProps'];

export const JournalEntriesModal = () => {
  'use memo';

  const { close: closeModal } = useJournalEntriesModal();

  const where = useJournalEntriesModalWhere();
  const { filter } = useJournalEntriesModalFilter();

  const queryData = useJournalEntriesQuery({
    skip: !where,
    modalFilter: filter,
    modalWhere: where,
  });

  const preFilter = where?.filter;

  const renderExcludedStatus = useCallback(
    (content: ReactNode) => (
      <HiddenFeatureIndicator permission="vrp-admin">
        {content}
      </HiddenFeatureIndicator>
    ),
    []
  );

  const filterProps = useMemo<FilterProps>(() => {
    const hide: FilterProps['hide'] = [];

    if (preFilter?.date) hide.push('date');
    if (preFilter?.status) hide.push('amount');
    if (preFilter?.listingIds || preFilter?.listingOwnershipPeriodIds)
      hide.push('listing');
    if (preFilter?.accountIds) hide.push('account');
    if (preFilter?.classifications) hide.push('classification');
    if (preFilter?.party) hide.push('party');
    if (preFilter?.ledger) hide.push('book');
    if (preFilter?.bookingChannels) hide.push('bookingChannel');

    return {
      hide: hide.length ? hide : undefined,
      view: 'all',
      renderExcludedStatus,
    };
  }, [preFilter, renderExcludedStatus]);

  return (
    <Modal
      opened={!!where}
      onClose={closeModal}
      title={'Journal Entries'}
      size={1600}
      zIndex={300}
      centered
      radius="lg"
      styles={{
        title: {
          fontWeight: 500,
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          paddingTop: 2,
          minHeight: '80dvh',
        },
      }}
    >
      <JournalEntriesTable filterProps={filterProps} queryData={queryData}>
        {({ disabled }) => (
          <GeneralLedgerExportButton report="detail" disabled={disabled} />
        )}
      </JournalEntriesTable>
    </Modal>
  );
};
